import store from '@/store'

import { $notification, $t, $loading } from '@/utils'
import { redirectByLogin } from './user'
import { getRouter } from '@/router'
import { getApExamSessionSnapshot, saveApExamSessionSnapshot } from './apexamSession'

const defaultErrorText = $t('login.appleLoginError')
const appleWebClientId = 'tech.dreame.solvemate.web.service'
export const prodAppleWebRedirectURI = 'https://console.mysovi.ai/api/solve-x-ai/user/apple/weblogin/callback'
export const uatAppleWebRedirectURI = 'https://console-uat.mysovi.ai/api/solve-x-ai/user/apple/weblogin/callback'
export const devAppleWebRedirectURI = 'https://solvexai.198.daxue.cloud/api/solve-x-ai/user/apple/weblogin/callback'

const applePopupFlowCacheKey = 'apple_popup_flow'
// 弹窗流程标记的有效期：超过这个时间认为是上一次被取消/拦截后残留的脏标记，避免误关主标签页、跳过登录后跳转
const applePopupFlowTTL = 5 * 60 * 1000

const resolveHost = () => {
    return typeof window !== 'undefined' ? window.location.hostname : ''
}

const getAppleWebRedirectURI = () => {
    const host = resolveHost()
    if (host === 'localhost' || host === '127.0.0.1' || host.endsWith('.198.daxue.cloud')) return devAppleWebRedirectURI
    if (host === 'uat.mysovi.ai' || host.endsWith('.sovi-ai-web-v2.pages.dev')) return uatAppleWebRedirectURI
    return prodAppleWebRedirectURI
}

const markApplePopupFlow = () => {
    localStorage.setItem(applePopupFlowCacheKey, String(Date.now()))
}

const isApplePopupFlow = () => {
    const raw = localStorage.getItem(applePopupFlowCacheKey)
    if (!raw) return false

    const startedAt = Number(raw)
    // 非数字（如旧版本写入的 '1'）或已过期都视为无效，并顺手清理
    if (!Number.isFinite(startedAt) || Date.now() - startedAt >= applePopupFlowTTL) {
        clearApplePopupFlow()
        return false
    }

    return true
}

const clearApplePopupFlow = () => {
    localStorage.removeItem(applePopupFlowCacheKey)
}

const buildAppleAuthorizeUrl = () => {
    const params = new URLSearchParams({
        client_id: appleWebClientId,
        redirect_uri: getAppleWebRedirectURI(),
        response_type: 'code',
        response_mode: 'form_post',
        scope: 'name email',
        state: 'apple-signin-' + Math.random().toString(36).substring(2, 15)
    })

    return `https://appleid.apple.com/auth/authorize?${params.toString()}`
}

// 处理 Apple OAuth2 回调
export const checkAppleLoginCallback = async () => {
    if (isAppleLoginCallback()) {
        $loading.loading()

        try {
            const hash = window.location.hash
            const params = new URLSearchParams(hash.substring(1))
            const token = params.get('apple_token')
            const user = params.get('user')
            const error = params.get('error')

            if (error) {
                if (error === 'user_cancelled_authorize') {
                    throw new Error(JSON.stringify({ status: 'cancel' }))
                } else {
                    throw new Error(JSON.stringify({ status: 'error', error: defaultErrorText + ': ' + params.get('error_description') }))
                }
            }

            if (!token) {
                throw new Error(JSON.stringify({ status: 'error', error: defaultErrorText + ': No access token' }))
            }

            await store.dispatch('user/appleLogin', {
                appleInfo: { identityToken: token, user: user }
            })

            if (typeof window !== 'undefined' && (window.name === 'SoviLoginPopup' || window.opener || isApplePopupFlow())) {
                clearApplePopupFlow()
                clearOAuthParams()
                window.close()
                return true
            }

            const redirectType = localStorage.getItem('apple_redirect_type')
            const redirectPath = localStorage.getItem('apple_redirect_path')
            localStorage.removeItem('apple_redirect_type')
            localStorage.removeItem('apple_redirect_path')

            if (redirectType === 'apexam-guest-resume' && redirectPath) {
                // 用户在 AP Exam 中通过 Apple 登录，需要回到原来的考试页面
                // 清除 sessionStorage 中的 guestMode，因为用户已登录
                const snapshot = getApExamSessionSnapshot()
                if (snapshot?.guestMode) {
                    saveApExamSessionSnapshot({ ...snapshot, guestMode: false })
                }
                getRouter().replace(redirectPath)
            } else {
                redirectByLogin(redirectType || undefined)
            }

            clearOAuthParams()

            $loading.hide()

            return true
        } catch (error: any) {
            console.error(error)
            try {
                const errorData = JSON.parse(error.message)

                if (errorData.status != 'cancel') {
                    $notification.error(defaultErrorText, errorData.error)
                }
            } catch (error: any) {
                $notification.error(defaultErrorText)
            }

            clearApplePopupFlow()
            clearOAuthParams()

            $loading.hide()

            return false
        }
    }
}

// 检查是否是 Apple 登录回调
export const isAppleLoginCallback = () => {
    const hash = window.location.hash

    return hash.includes('apple_token=')
}

// 清理URL中的OAuth参数
export const clearOAuthParams = () => {
    if (window.location.hash) {
        window.history.replaceState({}, document.title, window.location.pathname + window.location.search)
    }
}

export const appleLogin = async (redirectType: string) => {
    const isEmbedded = typeof window !== 'undefined' && window !== window.top;
    if (isEmbedded && redirectType !== 'standalone') {
        const width = 500;
        const height = 650;
        const left = (window.screen.width - width) / 2;
        const top = (window.screen.height - height) / 2;
        markApplePopupFlow()
        const popup = window.open(
            buildAppleAuthorizeUrl(),
            'SoviLoginPopup',
            'width=' + width + ',height=' + height + ',left=' + left + ',top=' + top + ',scrollbars=yes'
        );

        if (popup) {
            return;
        }

        // 弹窗被拦截（移动端浏览器/webview 常见）：清掉标记避免脏数据，回落到 SDK 整页登录流程
        clearApplePopupFlow()
    }

    // 走到这里说明是整页（非弹窗）登录路径，清掉可能残留的弹窗标记，避免回调时误判
    clearApplePopupFlow()

    try {
        const appleID = (window as any).AppleID

        if (appleID && appleID.auth) {
            localStorage.setItem('apple_redirect_type', redirectType)
            localStorage.setItem('apple_redirect_path', window.location.pathname + window.location.search)

            // 使用Apple ID SDK进行登录
            await appleID.auth.signIn()
        } else {
            console.error('Apple ID SDK 未初始化')
            // 如果SDK未初始化，可以尝试点击隐藏的按钮
            const hiddenButton = document.querySelector('#appleid-signin div[role="button"]') as HTMLElement

            if (hiddenButton) {
                hiddenButton.click()
            }
        }
    } catch (error) {
        console.error('Apple 登录失败:', error)
    }
}

// Apple ID 初始化重试计数
let appleIDInitRetryCount = 0
let maxRetryCount = 50 // 最大重试50次，总计5秒

// Apple ID 初始化函数
export const initAppleID = () => {
    return new Promise((resolve, reject) => {
        try {
            const appleID = (window as any).AppleID
            const appleSigninElement = document.getElementById('appleid-signin')

            if (appleID && appleID.auth && appleSigninElement) {
                appleID.auth.init({
                    clientId: appleWebClientId,
                    scope: 'name email',
                    redirectURI: getAppleWebRedirectURI(), // Apple requires exact absolute URL matching in Developer Console
                    state: 'apple-signin-' + Math.random().toString(36).substring(2, 15),
                    usePopup: false,
                    responseType: 'code'
                })

                // 渲染Apple ID登录按钮
                appleID.auth.renderButton()

                appleIDInitRetryCount = 0 // 重置计数器

                resolve(true)
            } else {
                appleIDInitRetryCount++

                if (appleIDInitRetryCount < maxRetryCount) {
                    const missingItems = []

                    if (!appleID) {
                        missingItems.push('AppleID SDK')
                    }

                    if (!appleSigninElement) {
                        missingItems.push('DOM元素#appleid-signin')
                    }

                    setTimeout(initAppleID, 100)
                } else {
                    reject(false)
                }
            }
        } catch (error) {
            appleIDInitRetryCount++

            if (appleIDInitRetryCount < maxRetryCount) {
                setTimeout(initAppleID, 100)
            }

            reject(false)
        }
    })
}
